/**
 * Auth gate: resolve the session, then either the Google sign-in screen, the game, or (admins)
 * /admin/feedback and /admin/games. Links into the game survive signing in: `/join/<id>` (an
 * invite: join that lobby, or watch it once started) and `/replay/<id>` (a finished game's replay).
 */
import { useCallback, useEffect, useState } from 'react';
import { t, type Me } from '@kadice/shared';
import { api } from './api';
import { FeedbackAdmin } from './components/FeedbackAdmin';
import { GameLogAdmin } from './components/GameLogAdmin';
import { PlayerAdmin } from './components/PlayerAdmin';
import { Login } from './components/Login';
import { Main, type DeepLink } from './components/Main';

/** `/join/<id>` or `/replay/<id>`, else null. */
function deepLink(path: string): DeepLink | null {
  const m = /^\/(join|replay)\/(\d{1,10})\/?$/.exec(path);
  return m ? { kind: m[1] as DeepLink['kind'], gameId: Number(m[2]) } : null;
}

export function App() {
  // undefined = still checking the session.
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.me().then(setMe, () => setError(t('app.unreachable')));
  }, []);

  const signedOut = useCallback(() => setMe(null), []);

  if (error) return <p className="center">{error}</p>;
  if (me === undefined) return <p className="center">{t('app.loading')}</p>;
  if (me === null) return <Login onSignedIn={setMe} />;
  if (location.pathname.startsWith('/admin/feedback')) return <FeedbackAdmin />;
  if (location.pathname.startsWith('/admin/players')) return <PlayerAdmin />;
  if (location.pathname.startsWith('/admin/games')) return <GameLogAdmin />;
  return (
    <Main
      onSignedOut={signedOut}
      isAdmin={!!me.isAdmin}
      askPlayedBefore={me.playedBefore === null}
      link={deepLink(location.pathname)}
    />
  );
}
